import React, { lazy, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";

import "./App.css";
import Header from "./components/header/Header";
import Homepage from "./pages/homepage/Homepage";
import Footer from "./components/footer/Footer";
// removed unused imports: HeaderTwo, AuthHeader
import { Provider } from "react-redux";
import { persistor, store } from "./state/store";
import { PersistGate } from "redux-persist/integration/react";
import LoadingComponent from "./components/loading/Loading";
import AdminLayout from "./components/admin-layout/AdminLayout";
import { ToastContainer } from "react-toastify";
import { LoaderProvider } from "./context/LoaderContext";
// Store pages are kept but not routed until the store launches
// import Store from "./pages/store/Store";
// import StoreReg from "./pages/store/reg/StoreReg";
// Hidden for now (routes commented out below)
// import Dgr from "./pages/digital/Dgr";
// import DgrListing from "./pages/digital/DgrListing";
// Hidden for now (routes fall through to Coming Soon)
// import Books from "./pages/books/Books";
// import BookmarkListing from "./pages/books/BookmarkListing";
import ScrollToTop from "./components/scroll-to-top/ScrollToTop";

// Pages load on demand so visitors don't download the whole site (and the
// admin area) before the first page shows. Styles stay global: see globalStyles.ts
const Donate = lazy(() => import("./pages/donate/Donate"));
const Give = lazy(() => import("./pages/give/Give"));
const Booking = lazy(() => import("./pages/booking/Booking"));
const BookingSlot = lazy(() => import("./pages/booking/BookingSlot"));
const Podcast = lazy(() => import("./pages/podcast/Podcast"));
const SinglePodcast = lazy(() => import("./pages/podcast/SinglePodcast"));
const Blog = lazy(() => import("./pages/blog/Blog"));
const BlogSingle = lazy(() => import("./pages/blog/BlogSingle"));
const Teachings = lazy(() => import("./pages/teachings/Teachings"));
const SingleTeaching = lazy(() => import("./pages/teachings/SingleTeaching"));
const About = lazy(() => import("./pages/about/About"));
const Register = lazy(() => import("./pages/auth/Register"));
const Verify = lazy(() => import("./pages/verify/Verify"));
const Dashboard = lazy(() => import("./pages/admin/dashboard/Dashboard"));
const BlogManage = lazy(() => import("./pages/admin/blog-manage/BlogManage"));
const BlogManageSingle = lazy(() => import("./pages/admin/blog-manage/BlogManageSingle"));
const CreateBlog = lazy(() => import("./pages/admin/blog-manage/CreateBlog"));
const VideoTeachings = lazy(() => import("./pages/admin/video-teachings/VideoTeachings"));
const SingleVideo = lazy(() => import("./pages/admin/video-teachings/SingleVideo"));
const Availabilities = lazy(() => import("./pages/admin/availabilities/Availabilities"));
const AddAvailabilities = lazy(() => import("./pages/admin/availabilities/AddAvailabilities"));
const ComingSoon = lazy(() => import("./pages/soon/ComingSoon"));
const Login = lazy(() => import("./pages/auth/Login"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const Otp = lazy(() => import("./pages/auth/Otp"));
const ManagePodcast = lazy(() => import("./pages/admin/podcast/ManagePodcast"));
const SinglePodcastAdmin = lazy(() => import("./pages/admin/podcast/SinglePodcast"));
const Categories = lazy(() => import("./pages/admin/blog-manage/categories/Categories"));
const VideoCategories = lazy(() => import("./pages/admin/video-teachings/categories/VideoCategories"));
const UserSingleVideo = lazy(() => import("./pages/teachings/UserSingleVideo"));
const Conferences = lazy(() => import("./pages/conferences/Conferences"));
const PPPConference = lazy(() => import("./pages/pppConference/PPPConference"));
const PPPConferenceSuccess = lazy(() => import("./pages/pppConference/PPPConferenceSuccess"));
const Belief = lazy(() => import("./pages/belief/Belief"));
const Chantal = lazy(() => import("./pages/chantal/Chantal"));
const Library = lazy(() => import("./pages/admin/library/Library"));
const BookDetail = lazy(() => import("./pages/digitalLibrary/BookDetail"));
const BookCheckout = lazy(() => import("./pages/digitalLibrary/BookCheckout"));
const AdminLogin = lazy(() => import("./pages/admin/auth/AdminLogin"));
const AdminOtp = lazy(() => import("./pages/admin/auth/AdminOtp"));
const PrayerRequests = lazy(() => import("./pages/admin/prayer-requests/PrayerRequests"));
const Testimonies = lazy(() => import("./pages/admin/testimonies/Testimonies"));
const ReviewedTestimonies = lazy(() => import("./pages/admin/testimonies/ReviewedTestimonies"));
const TestimonyPreview = lazy(() => import("./pages/admin/testimonies/TestimonyPreview"));
const Salvation = lazy(() => import("./pages/admin/salvation/Salvation"));
const Finance = lazy(() => import("./pages/admin/finance/Finance"));
const ConferenceRegistrations = lazy(() => import("./pages/admin/conferences/Conferences"));
const ConferenceManagement = lazy(() => import("./pages/admin/conferences/manage/ConferenceManagement"));
const ConferenceForm = lazy(() => import("./pages/admin/conferences/manage/ConferenceForm"));
const ConferenceDetails = lazy(() => import("./pages/admin/conferences/manage/ConferenceDetails"));
const MediaSpotlight = lazy(() => import("./pages/admin/media-spotlight/MediaSpotlight"));
const AdminManagement = lazy(() => import("./pages/admin/admins/AdminManagement"));
const Prayer = lazy(() => import("./pages/prayer/Prayer"));
const Events = lazy(() => import("./pages/events/Events"));
const Testimony = lazy(() => import("./pages/testimony/Testimony"));
const TestimonyGuide = lazy(() => import("./pages/testimony/TestimonyGuide"));
const NewBeliever = lazy(() => import("./pages/newBeliever/NewBeliever"));
const NewBelieverWelcome = lazy(() => import("./pages/newBeliever/NewBelieverWelcome"));
const Contact = lazy(() => import("./pages/contact/Contact"));
const GiveCancelled = lazy(() => import("./pages/give/GiveResult").then((m) => ({ default: m.GiveCancelled })));
const GiveSuccess = lazy(() => import("./pages/give/GiveResult").then((m) => ({ default: m.GiveSuccess })));
const BookCheckoutCancelled = lazy(() => import("./pages/digitalLibrary/BookCheckoutResult").then((m) => ({ default: m.BookCheckoutCancelled })));
const BookCheckoutReturn = lazy(() => import("./pages/digitalLibrary/BookCheckoutResult").then((m) => ({ default: m.BookCheckoutReturn })));

const hideHeaderRoutes = [
  "/login",
  "/register",
  "/verify",
  "/forgot-password",
  "/reset-password",
  "/admin/login",
  "/admin/otp",
  "/ppp-conference",
  "/ppp-conference/success",
];

function App() {
  return (
    <div className="App">
      <LoaderProvider>
        <ToastContainer />
        <Provider store={store}>
          <AppContent />
        </Provider>
      </LoaderProvider>
    </div>
  );
}

const storeSoon = {
  title: "Our store is opening soon",
  message:
    "Books, journals and resources to strengthen your walk with God are on their way. Check back soon to shop the collection.",
};

function AppContent() {
  const location = useLocation();
  // Admin route check
  const isAdminRoute = location.pathname.startsWith("/admin");
  // Pages inside AdminLayout, which draws its own top bar (no site nav or footer)
  const isAdminArea = isAdminRoute && !hideHeaderRoutes.includes(location.pathname);
  const hideHeader = isAdminArea || hideHeaderRoutes.includes(location.pathname);

  return (
    <>
      <ScrollToTop />
      {!hideHeader && <Header />}
      <PersistGate loading={<LoadingComponent />} persistor={persistor}>
        <Suspense fallback={<LoadingComponent />}>
        <Routes >
          <Route path="/" element={<Homepage />} />
          <Route path="/donate" element={<Donate />} />
          {/* New giving flow; not linked from the nav yet */}
          <Route path="/give" element={<Give />} />
          <Route path="/give/success" element={<GiveSuccess />} />
          <Route path="/give/cancelled" element={<GiveCancelled />} />
          <Route path="/prayer" element={<Prayer />} />
          <Route path="/events" element={<Events />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/testimony" element={<Testimony />} />
          <Route path="/testimony-guide" element={<TestimonyGuide />} />
          <Route path="/new-believer" element={<NewBeliever />} />
          <Route
            path="/new-believer/welcome"
            element={<NewBelieverWelcome />}
          />
          <Route path="/booking" element={<Booking />} />
          <Route path="/conferences" element={<Conferences />} />
          <Route path="/ppp-conference" element={<PPPConference />} />
          <Route path="/ppp-conference/success" element={<PPPConferenceSuccess />} />

          <Route path="/booking/slot" element={<BookingSlot />} />
          <Route path="/podcast" element={<Podcast />} />
          <Route path="/podcast/single/:id" element={<SinglePodcast />} />

          <Route path="/blog" element={<Blog />} />
          {/* Store isn't live yet; swap back to <Store /> and <StoreReg /> at launch */}
          <Route path="/store" element={<ComingSoon {...storeSoon} />} />
          <Route path="/store/reg" element={<ComingSoon {...storeSoon} />} />

          <Route path="/blog/single/:id" element={<BlogSingle />} />

          <Route path="/teachings" element={<Teachings />} />

          <Route path="/teachings/cats" element={<SingleTeaching />} />
          <Route path="/teachings/video/:id" element={<UserSingleVideo />} />

          <Route path="/about" element={<About />} />
          <Route path="/beliefs" element={<Belief />} />
          <Route path="/chantal" element={<Chantal />} />
          {/* Digital Library (/dgr) is hidden for now; these paths fall through to Coming Soon
          <Route path="/dgr" element={<Dgr />} />
          <Route path="/dgr-listing" element={<DgrListing />} /> */}
          {/* Books & Magazines listing is hidden for now; book pages open from the homepage slider */}
          <Route path="/books" element={<ComingSoon />} />
          {/* New Digital Library book pages and PDF checkout (not yet linked from /books) */}
          <Route path="/digital-library/:bookId" element={<BookDetail />} />
          <Route path="/digital-library/:bookId/checkout" element={<BookCheckout />} />
          <Route path="/digital-library/checkout/return" element={<BookCheckoutReturn />} />
          <Route path="/digital-library/checkout/cancelled" element={<BookCheckoutCancelled />} />
          <Route path="/bookmarks" element={<ComingSoon />} />





          <Route path="/register" element={<Register />} />

          <Route path="/verify" element={<Verify />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          <Route path="/otp" element={<Otp />} />

          {/* admin */}

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/otp" element={<AdminOtp />} />

          {/* Every other admin page shares the AdminLayout sidebar */}
          <Route element={<AdminLayout />}>
          <Route path="/admindashboard" element={<Dashboard />} />

          <Route path="/admin/podcast-management" element={<ManagePodcast />} />

          <Route
            path="/admin/single-podcast/:id"
            element={<SinglePodcastAdmin />}
          />

          <Route path="/admin/blog-management" element={<BlogManage />} />
          <Route path="/admin/blog-management/:id" element={<BlogManageSingle />} />

          <Route
            path="/admin/blog-management-single"
            element={<BlogManageSingle />}
          />

          <Route path="/admin/categories" element={<Categories />} />

          <Route path="/admin/create-blog" element={<CreateBlog />} />

          <Route path="/admin/video-teachings" element={<VideoTeachings />} />

          <Route
            path="admin/video-teachings/single/:id"
            element={<SingleVideo />}
          />

          <Route path="/admin/video-categories" element={<VideoCategories />} />

          <Route path="/admin/availabilities" element={<Availabilities />} />

          

          <Route path="/admin/library" element={<Library />} />

          <Route path="/admin/prayer-requests" element={<PrayerRequests />} />

          <Route path="/admin/testimonies" element={<Testimonies />} />
          <Route path="/admin/testimonies/reviewed" element={<ReviewedTestimonies />} />
          <Route path="/admin/testimonies/:id" element={<TestimonyPreview />} />

          <Route path="/admin/salvation" element={<Salvation />} />

          <Route path="/admin/finance" element={<Finance />} />

          <Route path="/admin/conferences" element={<ConferenceManagement view="recent" />} />
          <Route path="/admin/conferences/all" element={<ConferenceManagement view="all" />} />
          <Route path="/admin/conferences/new" element={<ConferenceForm />} />
          <Route path="/admin/conferences/:id" element={<ConferenceDetails />} />
          <Route path="/admin/conferences/:id/edit" element={<ConferenceForm key="edit" />} />
          <Route path="/admin/conferences/:id/registrations" element={<ConferenceRegistrations />} />

          <Route path="/admin/media-spotlight" element={<MediaSpotlight />} />

          <Route path="/admin/admins" element={<AdminManagement />} />


          <Route
            path="/admin/add-availabilities"
            element={<AddAvailabilities />}
          />

          {/* Sidebar links that don't have a page yet */}
          <Route path="/admin/*" element={<ComingSoon />} />
          </Route>

          <Route path="*" element={<ComingSoon />} />
        </Routes>
        </Suspense>
        {!isAdminRoute && !location.pathname.startsWith("/ppp-conference") && <Footer />}
      </PersistGate>
    </>
  );
}

export default App;
